import ArrowRightRounded from '@/assets/icons/arrow-right.svg'
import EarthImage from '@/assets/imgs/earth.svg'
import {
  fadeLeft,
  fadeRight,
  fadeUp,
  staggerContainer,
} from '@/core/constants/animations'
import { ROUTES } from '@/core/constants/routes'
import { capitalizeFirstLetter } from '@/core/lib/utils/capitalize-first-letter'
import { cn } from '@/core/lib/utils/cn'
import { Motion } from '@/core/ui/atoms/motion'
import { RemoteImage } from '@/core/ui/atoms/remote-image'
import { Divider } from '@/core/ui/components/divider'
import { SliderModel } from '@/features/content/types/models/slider.model'
import { Link } from '@/i18n/navigation'

import { getTranslations } from 'next-intl/server'

type Props = {
  className?: string
  slides: SliderModel[]
}

export const About: React.FC<Props> = async ({ className, slides }) => {
  const t = await getTranslations()

  if (!slides || !slides[0]) return null

  const slide = slides[0]

  return (
    <article className={cn('relative flex flex-col', className)}>
      <Motion
        type='h2'
        className='text-navy text-4xl font-semibold'
        initial='initial'
        whileInView='animate'
        viewport={{ once: true }}
        variants={fadeUp.variants}
        transition={fadeUp.transition}
        id='about-slide-title'
      >
        {capitalizeFirstLetter(t('landing.safeTravel'))}
      </Motion>
      <Divider className='mt-3' />
      <Motion
        type='section'
        aria-labelledby='about-slide-title'
        className='mt-16 flex flex-col items-start gap-8 self-stretch md:flex-row lg:gap-24'
        variants={staggerContainer}
        initial='initial'
        whileInView='animate'
        viewport={{ once: true, amount: 0.4 }}
      >
        <Motion
          type='figure'
          className='w-full flex-1'
          variants={fadeLeft.variants}
          transition={fadeLeft.transition}
          initial='initial'
          whileInView='animate'
          viewport={{ once: true }}
        >
          <RemoteImage
            alt={slide.title}
            width={600}
            height={600}
            className='w-full rounded-lg object-contain'
            src={slide.image.url}
          />
        </Motion>

        <Motion
          type='div'
          className='flex flex-1 flex-col gap-1'
          variants={fadeRight.variants}
          transition={fadeRight.transition}
        >
          <h3 className='text-grey text-lg font-medium'>
            {capitalizeFirstLetter(slide.title)}
          </h3>
          <div dangerouslySetInnerHTML={{ __html: slide.description }} />
          <Link
            href={ROUTES.about}
            className='text-sky-blue focus:ring-primary mt-8 flex w-fit items-center gap-2 rounded text-lg font-medium focus:ring-2 focus:outline-none'
            aria-label={t('ariaLabels.learnMore')}
            passHref
            target='_blank'
          >
            <span className='inline-flex items-center gap-2'>
              {t('links.learnMore')}
              <ArrowRightRounded
                className='transition-transform duration-150 ease-in-out rtl:rotate-180'
                aria-hidden='true'
              />
            </span>
          </Link>
        </Motion>
      </Motion>
      <EarthImage
        className='absolute right-0 bottom-0 -z-10'
        aria-hidden='true'
      />
    </article>
  )
}
